/**
 * Public unsubscribe page — resolves the signed token from the URL and
 * shows the same category checkboxes as the profile card so anyone can
 * update preferences without logging in.
 *
 * URL shape:
 *   /unsubscribe?token=<signed>&cat=<optional single-category shortcut>
 *
 * If `cat=` is present we pre-toggle that single category off and show
 * an inline confirmation banner so a one-click unsub from an email
 * footer feels instant (patient sees "You're unsubscribed from
 * Promotions & discounts" the moment the page loads).
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Bell, Mail, MessageSquare, ShieldCheck, Check, AlertTriangle } from "lucide-react";
import PublicFooter from "@/components/PublicFooter";

const API = process.env.REACT_APP_BACKEND_URL;

export default function UnsubscribePage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const catShortcut = params.get("cat") || "";

  const [categories, setCategories] = useState([]);
  const [prefs, setPrefs] = useState({ email: {}, sms: {} });
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedOnce, setSavedOnce] = useState(false);
  const [error, setError] = useState("");

  // Load current prefs; if there's a `cat=` shortcut, apply it immediately
  // and auto-save so single-click unsubs feel instant.
  useEffect(() => {
    if (!token) {
      setError("This unsubscribe link is missing its token. Please open the link from your most recent GLPFlo email.");
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/notify/unsub-page-data?token=${encodeURIComponent(token)}`);
        setCategories(data.categories || []);
        setEmail(data.email || "");
        let starting = data.prefs || { email: {}, sms: {} };

        if (catShortcut) {
          // One-click shortcut path — flip that category OFF and post immediately.
          const patchedEmail = { ...(starting.email || {}), [catShortcut]: false };
          const patchedSms = { ...(starting.sms || {}), [catShortcut]: false };
          starting = { email: patchedEmail, sms: patchedSms };
          try {
            const saved = await axios.post(`${API}/api/notify/unsubscribe`, {
              token,
              email: { [catShortcut]: false },
              sms: { [catShortcut]: false },
            });
            starting = saved.data.prefs;
            setSavedOnce(true);
          } catch { /* fall through — user can still hit Save */ }
        }
        setPrefs(starting);
      } catch (e) {
        setError(e?.response?.data?.detail || "This unsubscribe link is invalid or expired.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const shortcutCategoryLabel = useMemo(() => {
    return (categories.find((c) => c.key === catShortcut) || {}).label || "";
  }, [categories, catShortcut]);

  const toggle = (channel, key, val) =>
    setPrefs((p) => ({ ...p, [channel]: { ...(p[channel] || {}), [key]: val } }));

  const unsubEverything = () => {
    const next = { email: { ...(prefs.email || {}) }, sms: { ...(prefs.sms || {}) } };
    for (const c of categories) {
      if (!c.transactional) {
        next.email[c.key] = false;
        next.sms[c.key] = false;
      }
    }
    setPrefs(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/api/notify/unsubscribe`, {
        token,
        email: prefs.email,
        sms: prefs.sms,
      });
      setPrefs(data.prefs);
      setSavedOnce(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <p className="text-sm text-slate-500 dark:text-slate-400">Loading your preferences…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col">
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="max-w-md rounded-2xl border border-rose-200 dark:border-rose-800/60 bg-white dark:bg-slate-900 p-8 text-center">
            <AlertTriangle className="w-10 h-10 text-rose-600 mx-auto mb-3" />
            <h1 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Unsubscribe link problem</h1>
            <p className="text-sm text-slate-600 dark:text-slate-300">{error}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-4">
              You can also manage preferences by signing in →{" "}
              <Link to="/patient-profile#notification-prefs" className="text-purple-700 dark:text-purple-300 underline">Patient profile</Link>.
            </p>
          </div>
        </div>
        <PublicFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col" data-testid="unsub-page">
      <div className="flex-1">
        <div className="max-w-2xl mx-auto px-4 py-10">
          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 sm:p-8">
            <div className="flex items-start gap-3 mb-4">
              <div className="rounded-xl bg-purple-600/10 p-2">
                <Bell className="w-5 h-5 text-purple-700 dark:text-purple-300" />
              </div>
              <div>
                <h1 className="text-xl font-black text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Manage your GLPFlo notifications
                </h1>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  {email ? <>Signed in as <b>{email}</b>. </> : null}
                  Order & account-security messages are required by law and cannot be turned off.
                </p>
              </div>
            </div>

            {savedOnce && catShortcut && (
              <div className="rounded-xl border border-emerald-200 dark:border-emerald-800/60 bg-emerald-50 dark:bg-emerald-950/30 p-3 mb-4" data-testid="unsub-shortcut-confirm">
                <div className="flex items-center gap-2 text-sm text-emerald-800 dark:text-emerald-200">
                  <Check className="w-4 h-4" /> You&rsquo;re unsubscribed from <b>{shortcutCategoryLabel || catShortcut}</b>.
                  Adjust the toggles below if you&rsquo;d like more (or fewer) messages.
                </div>
              </div>
            )}

            <div className="flex justify-end mb-3">
              <Button variant="outline" size="sm" onClick={unsubEverything} data-testid="unsub-all-btn">
                Unsubscribe from everything (marketing only)
              </Button>
            </div>

            <div className="grid grid-cols-12 gap-3 px-2 pb-2 border-b border-slate-100 dark:border-slate-800 text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              <div className="col-span-6 md:col-span-7">Category</div>
              <div className="col-span-3 md:col-span-3 flex items-center justify-center gap-1">
                <Mail className="w-3.5 h-3.5" /> Email
              </div>
              <div className="col-span-3 md:col-span-2 flex items-center justify-center gap-1">
                <MessageSquare className="w-3.5 h-3.5" /> Text
              </div>
            </div>
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {categories.map((c) => (
                <div key={c.key} className="grid grid-cols-12 gap-3 items-center py-3 px-2" data-testid={`unsub-row-${c.key}`}>
                  <div className="col-span-6 md:col-span-7">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold text-sm text-slate-900 dark:text-white">{c.label}</div>
                      {c.transactional && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 rounded-full px-2 py-0.5">
                          <ShieldCheck className="w-3 h-3" /> Required
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{c.description}</div>
                  </div>
                  <div className="col-span-3 md:col-span-3 flex justify-center">
                    <Switch
                      checked={!!prefs.email?.[c.key]}
                      onCheckedChange={(v) => toggle("email", c.key, v)}
                      disabled={c.transactional}
                      data-testid={`unsub-email-${c.key}`}
                    />
                  </div>
                  <div className="col-span-3 md:col-span-2 flex justify-center">
                    <Switch
                      checked={!!prefs.sms?.[c.key]}
                      onCheckedChange={(v) => toggle("sms", c.key, v)}
                      disabled={c.transactional}
                      data-testid={`unsub-sms-${c.key}`}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-end gap-2 mt-5">
              <Button
                onClick={save}
                disabled={saving}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="unsub-save-btn"
              >
                {saving ? "Saving…" : savedOnce ? "Update preferences" : "Save preferences"}
              </Button>
            </div>

            {savedOnce && !catShortcut && (
              <p className="text-center text-xs text-emerald-700 dark:text-emerald-300 mt-3">
                Preferences saved. You may close this tab.
              </p>
            )}
          </div>
        </div>
      </div>
      <PublicFooter />
    </div>
  );
}
